import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Joyride, { Step } from 'react-joyride';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getTourStyles } from '@/components/tour/tourStyles';

import { useDashboard } from '@/components/hooks/useDashboard';
import { useEmpresa } from '@/components/hooks/useEmpresa';
import { useInstallPWA } from '@/components/pwa/useInstallPWA';
import { usePageHelp, useTourVisibility } from '@/components/hooks/usePageHelp';
import { GLASS_CARD } from '@/components/ui/glass';

import DashboardHeader from './DashboardHeader';
import DashboardMetrics from './DashboardMetrics';
import DashboardCharts from './DashboardCharts';
import DashboardChecklist from './DashboardChecklist';
import DashboardResumenAnual from './DashboardResumenAnual';
import DashboardSeccion from './DashboardSeccion';
import { getDashboardTourSteps } from '@/components/tour/company/DashboardTourSteps';

const containerVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.08 } },
};

const fadeVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.35, ease: 'easeOut' as const } },
};

const PWA_BANNER_DISMISSED_KEY = 'pwa_banner_dismissed';

// ── Skeletons con la misma silueta que las cards reales ─────────────────────
const shimmer =
  'relative overflow-hidden before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_1.6s_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/40 dark:before:via-white/10 before:to-transparent';

const bone = 'rounded-md bg-gray-200/70 dark:bg-gray-700/50';

const MetricCardSkeleton = () => (
  <div
    className={`relative isolate overflow-hidden rounded-2xl ${GLASS_CARD} h-full`}
  >
    <div className="p-3.5 sm:p-4 flex flex-col h-full justify-between gap-2 sm:gap-3">
      <div className="flex justify-between items-center gap-2">
        <div className={`h-3 w-16 ${bone} ${shimmer}`} />
        <div
          className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gray-200/70 dark:bg-gray-700/50 ${shimmer}`}
        />
      </div>
      <div className="flex-1 flex items-end">
        <div className={`h-7 sm:h-8 w-20 ${bone} ${shimmer}`} />
      </div>
      <div className="flex flex-col gap-1.5 min-h-[34px] justify-end">
        <div className={`h-3 w-10 ${bone} ${shimmer}`} />
        <div className={`h-2.5 w-20 ${bone} ${shimmer}`} />
      </div>
    </div>
  </div>
);

const MetricsSkeleton = () => (
  <div className="space-y-6">
    {[
      { n: 3, grid: 'grid-cols-2 sm:grid-cols-3' },
      { n: 5, grid: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5' },
      { n: 4, grid: 'grid-cols-2 lg:grid-cols-4' },
    ].map((s, i) => (
      <div key={i} className="space-y-3">
        <div className={`h-2.5 w-20 ${bone} ${shimmer}`} />
        <div className={`grid gap-3 sm:gap-4 ${s.grid}`}>
          {Array.from({ length: s.n }).map((_, j) => (
            <MetricCardSkeleton key={j} />
          ))}
        </div>
      </div>
    ))}
  </div>
);

const ChartCardSkeleton = ({ tall = false }: { tall?: boolean }) => (
  <div
    className={`relative isolate overflow-hidden rounded-2xl ${GLASS_CARD} p-6`}
  >
    <div className={`h-3 w-32 mb-6 ${bone} ${shimmer}`} />
    <div
      className={`${tall ? 'h-64' : 'h-48'} rounded-xl bg-gray-100/70 dark:bg-gray-800/40 ${shimmer}`}
    />
  </div>
);

const ChartsSkeleton = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
    <ChartCardSkeleton />
    <ChartCardSkeleton tall />
    <div className="md:col-span-2">
      <ChartCardSkeleton tall />
    </div>
    <div className="md:col-span-2">
      <ChartCardSkeleton tall />
    </div>
  </div>
);
// ──────────────────────────────────────────────────────────────────────────

const Dashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const empresaId = localStorage.getItem('id_empresa') || '';

  const ahora = new Date();
  const [mes, setMes] = useState(ahora.getMonth() + 1);
  const [anio, setAnio] = useState(ahora.getFullYear());
  const [runTour, setRunTour] = useState(false);
  const [preparandoTour, setPreparandoTour] = useState(false);
  const [tourSteps, setTourSteps] = useState<Step[]>([]);
  const [cargadoUnaVez, setCargadoUnaVez] = useState(false);
  const isMobile = window.innerWidth < 640;
  const { canShow, isIOS, install } = useInstallPWA();
  const pwaBannerDismissed =
    localStorage.getItem(PWA_BANNER_DISMISSED_KEY) === 'true';

  const { empresa, loadingEmpresa } = useEmpresa(empresaId);
  const { loading, stats, serviciosMes } = useDashboard(
    empresaId,
    mes,
    anio
  );

  const isLoadingData = loading || loadingEmpresa;
  const mostrarSkeleton = isLoadingData && !cargadoUnaVez;
  const seccionesAbiertas = preparandoTour || runTour;

  useEffect(() => {
    if (!isLoadingData) setCargadoUnaVez(true);
  }, [isLoadingData]);

  useEffect(() => {
    if (!preparandoTour) return;
    const id = window.setTimeout(() => {
      setTourSteps(getDashboardTourSteps());
      setRunTour(true);
      setPreparandoTour(false);
    }, 350);
    return () => window.clearTimeout(id);
  }, [preparandoTour]);

  const seleccionarMesAnio = (m: number, a: number) => {
    setMes(m);
    setAnio(a);
  };

  const pasos = [
    {
      titulo: t('dashboard.checklist.uploadLogo'),
      completado: !!empresa?.logo,
      ruta: '/panel-empresa/companyProfile',
    },
    {
      titulo: t('dashboard.checklist.addDescription'),
      completado: !!empresa?.descripcion,
      ruta: '/panel-empresa/companyProfile',
    },
    {
      titulo: t('dashboard.checklist.configSchedule'),
      completado:
        !!empresa &&
        Array.isArray(empresa.horario_empresa) &&
        empresa.horario_empresa.length > 0,
      ruta: '/panel-empresa/companyProfile',
    },
    {
      titulo: t('dashboard.checklist.addFeaturedPhoto'),
      completado:
        !!empresa &&
        Array.isArray(empresa.fotos) &&
        empresa.fotos.some((f) => f.destacado),
      ruta: '/panel-empresa/companyProfile',
    },
    {
      titulo: t('dashboard.checklist.createService'),
      completado:
        !!empresa &&
        Array.isArray(empresa.servicios) &&
        empresa.servicios.length > 0,
      ruta: '/panel-empresa/services',
    },
    {
      titulo: t('dashboard.checklist.createWorker'),
      completado:
        !!empresa &&
        Array.isArray(empresa.trabajadores) &&
        empresa.trabajadores.length > 0,
      ruta: '/panel-empresa/workers',
    },
  ];

  const completados = pasos.filter((p) => p.completado).length;
  const porcentaje = Math.round((completados / pasos.length) * 100);
  const showChecklist = !mostrarSkeleton && !loadingEmpresa && completados < pasos.length;

  usePageHelp(
    () => setPreparandoTour(true),
    !mostrarSkeleton && !showChecklist
  );
  useTourVisibility(runTour);

  if (showChecklist) {
    return (
      <DashboardChecklist
        pasos={pasos}
        porcentaje={porcentaje}
        showChecklist={showChecklist}
        navigate={navigate}
      />
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8 pb-28 lg:pb-0"
    >
      <DashboardHeader
        mes={mes}
        anio={anio}
        setMes={setMes}
        setAnio={setAnio}
        showPwaButton={canShow && pwaBannerDismissed}
        onPwaInstall={install}
        isIOS={isIOS}
      />

      <AnimatePresence mode="wait">
        {mostrarSkeleton ? (
          <motion.div
            key="metrics-skeleton"
            variants={fadeVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
          >
            <MetricsSkeleton />
          </motion.div>
        ) : (
          <motion.div
            key="metrics-real"
            variants={fadeVariants}
            initial="hidden"
            animate="visible"
          >
            <div
              className={`transition-opacity duration-200 ${
                loading ? 'opacity-60' : ''
              }`}
            >
              <DashboardMetrics stats={stats} forzarAbiertas={seccionesAbiertas} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        {mostrarSkeleton ? (
          <motion.div
            key="charts-skeleton"
            variants={fadeVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
          >
            <ChartsSkeleton />
          </motion.div>
        ) : (
          <motion.div
            key="charts-real"
            variants={fadeVariants}
            initial="hidden"
            animate="visible"
          >
            <div className="space-y-6">
              <DashboardSeccion
                id="semana"
                titulo={t('dashboard.sections.weekAndServices')}
                forzarAbierta={seccionesAbiertas}
              >
                <DashboardCharts
                  empresaId={empresaId}
                  serviciosMes={serviciosMes}
                  mes={mes}
                  anio={anio}
                />
              </DashboardSeccion>
              <DashboardSeccion
                id="resumen"
                titulo={t('dashboard.sections.yearSummary')}
                forzarAbierta={seccionesAbiertas}
              >
                <DashboardResumenAnual
                  empresaId={empresaId}
                  anio={anio}
                  mes={mes}
                  onSelectMes={seleccionarMesAnio}
                />
              </DashboardSeccion>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Joyride
        steps={tourSteps}
        run={runTour}
        continuous
        showSkipButton
        spotlightClicks
        disableOverlayClose={false}
        locale={{
          back: t('profiles.tour.back'),
          close: t('profiles.tour.close'),
          last: t('profiles.tour.last'),
          next: t('profiles.tour.next'),
          skip: t('profiles.tour.skip'),
        }}
        callback={({ status }) => {
          if (status === 'finished' || status === 'skipped') setRunTour(false);
        }}
        styles={getTourStyles(isMobile)}
      />
    </motion.div>
  );
};

export default Dashboard;